import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Field, Select, Input, Empty, ModeBadge } from "@/components/kit";
import { CHANNELS } from "@/lib/constants";
import { Sparkles, Loader2, Wand2, AlertTriangle } from "lucide-react";

const PERIODS = [1, 7, 15, 30];
const ANTI = [30, 60, 90, 180, 365];
const OMNI_DEFAULT = ["Instagram", "Facebook", "WhatsApp", "TikTok"];

export default function Campanhas() {
  const { activeOrgId } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const draft = (() => { try { return JSON.parse(localStorage.getItem("pf_campaign_draft")) || {}; } catch { return {}; } })();
  const [mode, setMode] = useState("omni");
  const [theme, setTheme] = useState(params.get("theme") || (draft.theme ?? "Prevenção de recaída"));
  const [dup, setDup] = useState(null);
  const [days, setDays] = useState(draft.days ?? 30);
  const [channels, setChannels] = useState(draft.channels ?? CHANNELS);
  const [omniChannels, setOmniChannels] = useState(draft.omniChannels ?? OMNI_DEFAULT);
  const [anti, setAnti] = useState(draft.anti ?? 90);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    localStorage.setItem("pf_campaign_draft", JSON.stringify({ theme, days, channels, omniChannels, anti }));
  }, [theme, days, channels, omniChannels, anti]);

  useEffect(() => {
    if (!activeOrgId) return;
    const want = params.get("brand");
    client.get("/brands").then((r) => {
      setBrands(r.data);
      const pick = r.data.find((b) => b.id === want) || r.data[0];
      if (pick) setBrandId(pick.id);
    });
  }, [activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDup(null);
    if (!brandId || theme.trim().length < 4) return;
    const t = setTimeout(() => {
      client.post("/ideas/check", { brand_id: brandId, text: theme }).then((r) => setDup(r.data.match)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [brandId, theme, result]);

  const toggleChannel = (c) => setChannels(channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c]);
  const toggleOmni = (c) => setOmniChannels(omniChannels.includes(c) ? omniChannels.filter((x) => x !== c) : [...omniChannels, c]);

  const generate = async () => {
    if (!brandId) return toast.error("Selecione uma marca");
    if (!channels.length) return toast.error("Selecione ao menos um canal");
    setBusy(true); setResult(null);
    try {
      const { data } = await client.post("/campaigns", { brand_id: brandId, theme, days: Number(days), channels, anti_repeat_days: anti });
      setResult(data);
      if (data.mode === "REAL") toast.success(`Planejamento gerado com IA (${data.count} conteúdos)`);
      else toast.warning(`Planejamento gerado em modo ${data.mode}${data.ai_error ? " — IA indisponível" : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const genAllImages = async () => {
    const cid = result?.campaign?.id || result?.campaign_id;
    if (!cid) { toast.error("Não encontrei a campanha para gerar as artes. Gere um planejamento primeiro."); return; }
    try {
      const { data } = await client.post(`/campaigns/${cid}/images`);
      if (data.started) toast.success(`Gerando as artes de ${data.days} dia(s) em segundo plano. Elas aparecem na Central de Aprovação em instantes.`);
      else toast.info(data.message || "Nada a gerar.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const generateOmni = async () => {
    if (!brandId) return toast.error("Selecione uma marca");
    if (!omniChannels.length) return toast.error("Selecione ao menos uma rede");
    setBusy(true); setResult(null);
    try {
      const { data } = await client.post("/campaigns/omni", { brand_id: brandId, theme, channels: omniChannels, pillar: theme === params.get("theme") ? params.get("pillar") : null });
      setResult(data);
      if (data.mode === "REAL") toast.success(`Omni-canal gerado com IA (${data.count} formatos)`);
      else toast.warning(`Gerado em modo ${data.mode}${data.ai_error ? " — IA indisponível" : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!brands.length) return <div><PageHeader title="Gerador de Campanhas com IA" /><Empty>Crie uma marca em Empresas primeiro.</Empty></div>;

  return (
    <div>
      <PageHeader title="Gerador de Campanhas com IA" subtitle="Planejamento editorial ou 1 tema adaptado para todas as redes de uma vez."
        actions={<span className="hidden sm:inline"><ModeBadge mode="REAL" /> <span className="text-xs text-slate-400 ml-1">Geração de texto</span></span>} />

      <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 mb-6" data-testid="campaign-mode-tabs">
        <button type="button" data-testid="mode-omni" onClick={() => { setMode("omni"); setResult(null); }}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${mode === "omni" ? "pf-gradient text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          Omni-canal · 1 tema → todas as redes
        </button>
        <button type="button" data-testid="mode-plan" onClick={() => { setMode("plan"); setResult(null); }}
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${mode === "plan" ? "pf-gradient text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          Planejamento editorial
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {mode === "plan" ? (
        <Card className="lg:col-span-2 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Empresa / Marca">
              <Select data-testid="campaign-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            </Field>
            <Field label="Período">
              <Select data-testid="campaign-days" value={days} onChange={(e) => setDays(e.target.value)}>
                {PERIODS.map((p) => <option key={p} value={p}>{p} {p === 1 ? "dia" : "dias"}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="Tema principal"><Input data-testid="campaign-theme" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Ex.: prevenção de recaída após 6 meses" />{dup && (
            <div data-testid="theme-dup-warning" className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Tema já usado em {new Date(dup.used_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}: “{dup.text}”. Você pode gerar mesmo assim.</span>
            </div>
          )}</Field>
          <Field label="Anti-repetição de temas">
            <Select data-testid="campaign-anti" value={anti} onChange={(e) => setAnti(Number(e.target.value))}>
              {ANTI.map((a) => <option key={a} value={a}>Não repetir nos últimos {a} dias</option>)}
            </Select>
          </Field>
          <Field label="Canais">
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((c) => {
                const on = channels.includes(c);
                return <button key={c} type="button" data-testid={`channel-${c}`} onClick={() => toggleChannel(c)}
                  className={`rounded-full px-3 py-1.5 text-sm border transition-all ${on ? "pf-gradient text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{on ? "✓ " : ""}{c}</button>;
              })}
            </div>
          </Field>
          <Btn data-testid="generate-campaign-btn" onClick={generate} disabled={busy} className="w-full sm:w-auto">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} {busy ? "Gerando com IA..." : "Gerar planejamento editorial"}
          </Btn>
        </Card>
        ) : (
        <Card className="lg:col-span-2 space-y-5">
          <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3 text-sm text-indigo-700">
            Escreva <b>um tema</b> e a IA cria uma versão pronta e adaptada para cada rede selecionada — cada uma vira um card separado em <b>Aprovação</b>.
          </div>
          <Field label="Empresa / Marca">
            <Select data-testid="omni-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          </Field>
          <Field label="Tema"><Input data-testid="omni-theme" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Ex.: 5 sinais de que é hora de buscar ajuda" />{dup && (
            <div data-testid="theme-dup-warning" className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Tema já usado em {new Date(dup.used_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}: “{dup.text}”. Você pode gerar mesmo assim.</span>
            </div>
          )}</Field>
          <Field label="Redes">
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((c) => {
                const on = omniChannels.includes(c);
                return <button key={c} type="button" data-testid={`omni-channel-${c}`} onClick={() => toggleOmni(c)}
                  className={`rounded-full px-3 py-1.5 text-sm border transition-all ${on ? "pf-gradient text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{on ? "✓ " : ""}{c}</button>;
              })}
            </div>
          </Field>
          <Btn data-testid="generate-omni-btn" onClick={generateOmni} disabled={busy} className="w-full sm:w-auto">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} {busy ? "Adaptando com IA..." : "Gerar para todas as redes"}
          </Btn>
        </Card>
        )}

        <Card>
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Sparkles size={18} className="text-[#3457d5]" /> Resultado</h3>
          {result ? (
            <div className="space-y-3" data-testid="campaign-result">
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-700">
                <b>{result.count}</b> conteúdos criados · modo <b>{result.mode}</b>
              </div>
              {result.ai_error && <div className="text-xs text-amber-600">IA indisponível: {result.ai_error}</div>}
              {(result?.campaign?.id || result?.campaign_id) && (
                <Btn variant="primary" className="w-full" data-testid="gen-all-images-btn" onClick={genAllImages}>
                  <Sparkles size={16} /> Gerar todas as artes (1 clique)
                </Btn>
              )}
              <Btn variant="secondary" className="w-full" data-testid="goto-approval" onClick={() => navigate("/app/aprovacao")}>Ir para aprovação</Btn>
              <Btn variant="ghost" className="w-full" data-testid="goto-calendar" onClick={() => navigate("/app/calendario")}>Ver no calendário</Btn>
            </div>
          ) : <p className="text-sm text-slate-400">Configure e gere. Os conteúdos entram como <b>Pendentes de Aprovação</b>.</p>}
        </Card>
      </div>
    </div>
  );
}
